import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import {
  getEntityColors,
  getEntityIcon,
  getEntityRingClass
} from './EntityRelationshipCard';

const defaultTranslate = (text, fallback) => fallback || text;

const fallbackId = (entity) => entity?.id ?? entity?.Id ?? entity?.ID ?? null;
const fallbackName = (entity) => entity?.name ?? entity?.Name ?? entity?.title ?? entity?.Title ?? '';

const getCustomerType = (entity) => {
  if (!entity) return undefined;
  const raw = entity.CustomerType || entity.customerType || entity.Type || entity.type;
  if (!raw) return undefined;
  const normalized = String(raw).toLowerCase();
  if (normalized.includes('person') || normalized.includes('individual')) {
    return 'Person';
  }
  if (normalized.includes('company') || normalized.includes('business') || normalized.includes('organisation') || normalized.includes('organization')) {
    return 'Company';
  }
  return undefined;
};

const getCustomerCompanyName = (entity, customerType = getCustomerType(entity)) => {
  if (!entity) return undefined;
  const resolvedType = customerType || getCustomerType(entity);
  const candidates = resolvedType === 'Person'
    ? [
        entity.PersonCompanyName,
        entity.PersonCompanyName_Eng,
        entity.CompanyName,
        entity.CompanyName_Eng,
        entity.AccountName,
        entity.OrganizationName,
        entity.OrgName,
        entity.CustomerName,
        entity.CustomerName_Eng,
        entity.CompanyCustomerName,
        entity.CompanyCustomerName_Eng,
        entity?.Company?.CompanyName,
        entity?.Company?.CompanyName_Eng
      ]
    : [
        entity.CompanyName,
        entity.CompanyName_Eng,
        entity.CustomerName,
        entity.CustomerName_Eng,
        entity.AccountName,
        entity.OrganizationName,
        entity.OrgName,
        entity.Name
      ];
  const found = candidates.find(val => typeof val === 'string' && val.trim() !== '');
  return found ? found.trim() : undefined;
};

const getCustomerCompanyId = (entity, customerType = getCustomerType(entity)) => {
  if (!entity) return undefined;
  const keys = [
    'PersonCompanyID',
    'PersonCompanyId',
    'CompanyID',
    'CompanyId',
    'CompanyCustomerID',
    'CompanyCustomerId',
    'ParentCustomerID',
    'ParentCustomerId',
    'PrimaryCompanyID',
    'PrimaryCompanyId',
    'LinkedCompanyID',
    'LinkedCompanyId'
  ];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(entity, key)) {
      const value = entity[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        const trimmed = String(value).trim();
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? String(numeric) : trimmed;
      }
    }
  }
  if (customerType && customerType.toLowerCase() === 'person' && entity.Company) {
    return getCustomerCompanyId(entity.Company, 'Company');
  }
  if (customerType && customerType.toLowerCase() === 'company') {
    const fallback = entity.CustomerID || entity.CustomerId || entity.Id || entity.ID;
    if (fallback !== undefined && fallback !== null && String(fallback).trim() !== '') {
      const trimmed = String(fallback).trim();
      const numeric = Number(trimmed);
      return Number.isFinite(numeric) ? String(numeric) : trimmed;
    }
  }
  return undefined;
};

const getCustomerKey = (entity) => {
  if (!entity) return null;
  const companyId = getCustomerCompanyId(entity);
  if (companyId) return `id:${companyId}`;
  const companyName = getCustomerCompanyName(entity);
  if (companyName) return `name:${companyName.toLowerCase()}`;
  return null;
};

const sortCustomerResults = (list) => {
  if (!Array.isArray(list) || list.length <= 1) return list;
  const companies = [];
  const personsByKey = new Map();
  const orphans = [];

  list.forEach((entity, index) => {
    const type = (getCustomerType(entity) || '').toLowerCase();
    if (type === 'company') {
      const key = getCustomerKey(entity) || `company:${index}`;
      companies.push({ key, entity, index });
      if (!personsByKey.has(key)) personsByKey.set(key, []);
    } else if (type === 'person') {
      const key = getCustomerKey(entity);
      if (key) {
        if (!personsByKey.has(key)) personsByKey.set(key, []);
        personsByKey.get(key).push({ entity, index });
      } else {
        orphans.push({ entity, index });
      }
    } else {
      orphans.push({ entity, index });
    }
  });

  companies.sort((a, b) => a.index - b.index);
  personsByKey.forEach(listForCompany => listForCompany.sort((a, b) => a.index - b.index));
  orphans.sort((a, b) => a.index - b.index);

  const ordered = [];
  const used = new Set();

  companies.forEach(({ key, entity }) => {
    ordered.push(entity);
    used.add(entity);
    const linkedPersons = personsByKey.get(key);
    if (linkedPersons) {
      linkedPersons.forEach(({ entity: person }) => {
        if (!used.has(person)) {
          ordered.push(person);
          used.add(person);
        }
      });
      personsByKey.delete(key);
    }
  });

  const remaining = [...Array.from(personsByKey.values()).flat(), ...orphans];
  remaining.sort((a, b) => a.index - b.index);
  remaining.forEach(({ entity }) => {
    if (!used.has(entity)) {
      ordered.push(entity);
      used.add(entity);
    }
  });

  list.forEach(entity => {
    if (!used.has(entity)) ordered.push(entity);
  });

  return ordered;
};

const EntitySelectionModal = ({
  isOpen,
  entityType,
  title,
  subtitle,
  searchTerm = '',
  onSearchTermChange,
  onSearchChange,
  searchPlaceholder,
  loading = false,
  loadingMessage,
  idleMessage,
  results = [],
  emptyMessage,
  selectedIds = [],
  onToggleEntity,
  onToggle,
  onClose,
  onCancel,
  onConfirm,
  confirmLabel,
  confirmIcon: ConfirmIcon,
  confirmDisabled = false,
  cancelLabel,
  resolveId,
  resolveName,
  resolveSubInfo,
  getEntityId,
  getEntityName,
  t = defaultTranslate
}) => {
  const inputRef = useRef(null);

  const handleCancel = onCancel || onClose;
  const toggleHandler = onToggle || onToggleEntity;
  const searchHandler = onSearchChange || onSearchTermChange;
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [opportunityStageFilter, setOpportunityStageFilter] = useState('all');
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const [activityDateFilter, setActivityDateFilter] = useState('all');

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCustomerTypeFilter('all');
      setOpportunityStageFilter('all');
      setLeadStatusFilter('all');
      setActivityTypeFilter('all');
      setActivityDateFilter('all');
    }
  }, [isOpen]);

  const normalizedSelectedIds = useMemo(
    () => new Set(selectedIds.map(value => String(value))),
    [selectedIds]
  );

  const effectiveEntityType = entityType || 'Entity';
  const defaultRingClass = getEntityRingClass(effectiveEntityType);

  const headerTitle = title || `${t('common.select', 'Select')} ${t(`activities.entityTypes.${effectiveEntityType.toLowerCase()}`, effectiveEntityType)}`;
  const searchLabel = searchPlaceholder || t('common.search', 'Search');
  const emptyState = emptyMessage || t('common.noResultsFound', 'No results found');
  const idleState = idleMessage || t('common.startTypingToSearch', 'Start typing to search');
  const loadingState = loadingMessage || t('common.searching', 'Searching...');
  const cancelText = cancelLabel || t('common.cancel', 'Cancel');
  const confirmText = confirmLabel || t('common.save', 'Save');

  const getId = (entity, type) => {
    if (resolveId) return resolveId(entity, type);
    if (getEntityId) return getEntityId(entity, type);
    return fallbackId(entity);
  };

  const getName = (entity, type) => {
    if (resolveName) return resolveName(entity, type);
    if (getEntityName) return getEntityName(entity, type);
    return fallbackName(entity);
  };

  const getSubInfo = (entity, type) => {
    if (resolveSubInfo) return resolveSubInfo(entity, type);
    return null;
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && handleCancel) {
      handleCancel();
    }
  };

  const handleSearchInputChange = (event) => {
    searchHandler?.(event.target.value);
  };

  const isCustomerType = effectiveEntityType.toLowerCase() === 'customer';
  const isOpportunityType = effectiveEntityType.toLowerCase() === 'opportunity';
  const isLeadType = effectiveEntityType.toLowerCase() === 'lead';
  const isActivityType = effectiveEntityType.toLowerCase() === 'activity';

  // Dynamically extract unique stage values from results for opportunities
  const opportunityStages = useMemo(() => {
    if (!isOpportunityType || !results || results.length === 0) {
      return [{ key: 'all', label: t('common.all', 'All') }];
    }
    
    // Extract unique stages from the data
    const stageSet = new Set();
    results.forEach(entity => {
      const stage = entity?.Stage || entity?.stage;
      if (stage && typeof stage === 'string' && stage.trim()) {
        stageSet.add(stage.trim());
      }
    });
    
    // Convert to array and sort
    const stages = Array.from(stageSet).sort();
    
    // Build options with "All" first, then dynamic stages
    const options = [{ key: 'all', label: t('common.all', 'All') }];
    stages.forEach(stage => {
      options.push({ key: stage.toLowerCase(), label: stage });
    });
    
    return options;
  }, [isOpportunityType, results, t]);

  // Dynamically extract unique status values from results for leads
  const leadStatuses = useMemo(() => {
    if (!isLeadType || !results || results.length === 0) {
      return [{ key: 'all', label: t('common.all', 'All') }];
    }
    
    // Extract unique statuses from the data
    const statusSet = new Set();
    results.forEach(entity => {
      const status = entity?.Status || entity?.status;
      if (status && typeof status === 'string' && status.trim()) {
        statusSet.add(status.trim());
      }
    });
    
    // Convert to array and sort
    const statuses = Array.from(statusSet).sort();
    
    // Build options with "All" first, then dynamic statuses
    const options = [{ key: 'all', label: t('common.all', 'All') }];
    statuses.forEach(status => {
      options.push({ key: status.toLowerCase(), label: status });
    });
    
    return options;
  }, [isLeadType, results, t]);

  // Dynamically extract unique activity type values from results
  const activityTypes = useMemo(() => {
    if (!isActivityType || !results || results.length === 0) {
      return [{ key: 'all', label: t('common.all', 'All') }];
    }
    
    // Extract unique activity types from the data
    const typeSet = new Set();
    results.forEach(entity => {
      const actType = entity?.ActivityType || entity?.activityType || entity?.Type || entity?.type;
      if (actType && typeof actType === 'string' && actType.trim()) {
        typeSet.add(actType.trim());
      }
    });
    
    // Convert to array and sort
    const types = Array.from(typeSet).sort();
    
    // Build options with "All" first, then dynamic types
    const options = [{ key: 'all', label: t('common.all', 'All') }];
    types.forEach(type => {
      options.push({ key: type.toLowerCase(), label: type });
    });
    
    return options;
  }, [isActivityType, results, t]);

  // Helper function to check if a date is in the current month
  const isDateInCurrentMonth = (dateValue) => {
    if (!dateValue) return false;
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return false;
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    } catch {
      return false;
    }
  };

  const filteredResults = useMemo(() => {
    let filtered = results;

    // Filter by customer type
    if (isCustomerType && customerTypeFilter !== 'all') {
      filtered = filtered.filter((entity) => {
        const rawType = entity?.CustomerType || entity?.customerType || entity?.Type || entity?.type;
        if (!rawType) return customerTypeFilter === 'other';
        const normalized = String(rawType).toLowerCase();
        if (customerTypeFilter === 'person') {
          return normalized === 'person';
        }
        if (customerTypeFilter === 'company') {
          return normalized === 'company';
        }
        return true;
      });
    }

    // Filter by opportunity stage
    if (isOpportunityType && opportunityStageFilter !== 'all') {
      filtered = filtered.filter((entity) => {
        const stage = entity?.Stage || entity?.stage || '';
        return stage.toLowerCase() === opportunityStageFilter.toLowerCase();
      });
    }

    // Filter by lead status
    if (isLeadType && leadStatusFilter !== 'all') {
      filtered = filtered.filter((entity) => {
        const status = entity?.Status || entity?.status || '';
        return status.toLowerCase() === leadStatusFilter.toLowerCase();
      });
    }

    // Filter by activity type
    if (isActivityType && activityTypeFilter !== 'all') {
      filtered = filtered.filter((entity) => {
        const actType = entity?.ActivityType || entity?.activityType || entity?.Type || entity?.type || '';
        return actType.toLowerCase() === activityTypeFilter.toLowerCase();
      });
    }

    // Filter by activity date (this month)
    if (isActivityType && activityDateFilter === 'thismonth') {
      filtered = filtered.filter((entity) => {
        // Check multiple date fields
        const dateFields = [
          entity?.DueDate, entity?.dueDate,
          entity?.StartDate, entity?.startDate,
          entity?.EndDate, entity?.endDate,
          entity?.CreatedAt, entity?.createdAt,
          entity?.FollowUpDate, entity?.followUpDate
        ];
        return dateFields.some(date => isDateInCurrentMonth(date));
      });
    }

    return filtered;
  }, [results, isCustomerType, customerTypeFilter, isOpportunityType, opportunityStageFilter, isLeadType, leadStatusFilter, isActivityType, activityTypeFilter, activityDateFilter]);

  const orderedResults = useMemo(() => {
    if (!isCustomerType) return filteredResults;
    const ordered = sortCustomerResults(filteredResults);
    const unchanged = ordered.length === filteredResults.length && ordered.every((entity, idx) => entity === filteredResults[idx]);
    return unchanged ? filteredResults : ordered;
  }, [filteredResults, isCustomerType]);

  // Build a set of company IDs that are visible in the current result set
  const visibleCompanyIds = useMemo(() => {
    if (!isCustomerType) return new Set();
    const companyIds = new Set();
    orderedResults.forEach(entity => {
      const type = (getCustomerType(entity) || '').toLowerCase();
      if (type === 'company') {
        const companyId = getCustomerCompanyId(entity, 'Company');
        if (companyId) companyIds.add(companyId);
      }
    });
    return companyIds;
  }, [orderedResults, isCustomerType]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110]"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {headerTitle}
            </h3>
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
            type="button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-3">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchInputChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder={searchLabel}
          />
        </div>

        {isCustomerType && (
          <div className="px-5 pb-3">
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                {t('customers.filterByType', 'Type')}:
              </span>
              {[
                { key: 'all', label: t('customers.allTypes', 'All') },
                { key: 'person', label: t('customers.personType', 'Person') },
                { key: 'company', label: t('customers.companyType', 'Company') }
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setCustomerTypeFilter(option.key)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                    customerTypeFilter === option.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {isOpportunityType && (
          <div className="px-5 pb-3">
            <div className="flex items-center flex-wrap gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                {t('opportunities.filterByStage', 'Stage')}:
              </span>
              {opportunityStages.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setOpportunityStageFilter(option.key)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                    opportunityStageFilter === option.key
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {isLeadType && (
          <div className="px-5 pb-3">
            <div className="flex items-center flex-wrap gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                {t('leads.filterByStatus', 'Status')}:
              </span>
              {leadStatuses.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setLeadStatusFilter(option.key)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                    leadStatusFilter === option.key
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {isActivityType && (
          <div className="px-5 pb-3 space-y-2">
            {/* Activity Type Filter */}
            <div className="flex items-center flex-wrap gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                {t('activities.filterByType', 'Type')}:
              </span>
              {activityTypes.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setActivityTypeFilter(option.key)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                    activityTypeFilter === option.key
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {/* Activity Date Filter */}
            <div className="flex items-center flex-wrap gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                {t('activities.filterByDate', 'Date')}:
              </span>
              {[
                { key: 'all', label: t('common.all', 'All') },
                { key: 'thismonth', label: t('activities.thisMonth', 'This Month') }
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setActivityDateFilter(option.key)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                    activityDateFilter === option.key
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-5 pb-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              {loadingState}
            </div>
          ) : orderedResults.length === 0 ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              {searchTerm.trim() ? emptyState : idleState}
            </div>
          ) : (
            <div className="space-y-3">
              {orderedResults.map((entity, index) => {
                const itemType = entity?.EntityType || effectiveEntityType;
                const itemCustomerType = itemType === 'Customer'
                  ? (entity?.CustomerType || entity?.customerType || entity?.Type || entity?.type)
                  : undefined;
                const itemColors = getEntityColors(itemType, itemCustomerType);
                const itemRingClass = getEntityRingClass(itemType, itemCustomerType) || defaultRingClass;
                const id = getId(entity, itemType);
                const idString = String(id ?? index);
                const name = getName(entity, itemType) || `${itemType} ${idString}`;
                const companyName = itemType === 'Customer' ? getCustomerCompanyName(entity, itemCustomerType) : undefined;
                const isPerson = itemType === 'Customer' && (itemCustomerType || '').toLowerCase() === 'person';
                
                // Get customer/company name for opportunities and leads
                const isOpportunity = itemType.toLowerCase() === 'opportunity';
                const isLead = itemType.toLowerCase() === 'lead';
                
                // For opportunities, check multiple possible field names for customer/company
                const opportunityCustomerName = isOpportunity 
                  ? (entity?.CustomerName || entity?.customerName || 
                     entity?.Customer?.CompanyName || entity?.Customer?.CustomerName ||
                     entity?.customer?.companyName || entity?.customer?.customerName ||
                     entity?.CompanyName || entity?.companyName ||
                     entity?.ContactName || entity?.contactName)
                  : undefined;
                
                // For leads, show CustomerName (from linked customer) if available and different from subInfo
                const leadCustomerName = isLead && entity?.CustomerName 
                  ? entity.CustomerName 
                  : undefined;
                
                // Only indent person if their company is visible in the current results
                const personCompanyId = isPerson ? getCustomerCompanyId(entity, 'Person') : null;
                const shouldIndent = isPerson && personCompanyId && visibleCompanyIds.has(personCompanyId);
                
                let subInfo = getSubInfo(entity, itemType);
                
                // Don't show subInfo if it's the same as the name (avoid duplication for Company customers)
                if (subInfo && name && subInfo.trim().toLowerCase() === name.trim().toLowerCase()) {
                  subInfo = null;
                }
                
                const selected = normalizedSelectedIds.has(idString);

                return (
                  <button
                    key={idString}
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      toggleHandler?.(entity, itemType);
                    }}
                    className={`w-full text-left p-2 border-2 rounded-lg transition-colors ${itemColors.border} ${itemColors.bg} ${itemColors.hoverBorder} ${itemColors.hoverBg} ${selected ? `${itemRingClass} ring-2 ring-offset-1` : ''} ${shouldIndent ? 'ml-6' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        {getEntityIcon(itemType, 'h-4 w-4', itemCustomerType)}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {name}
                            </span>
                            {isPerson && companyName && (
                              <span className="text-xs font-normal text-blue-600 dark:text-blue-400 truncate">
                                @ {companyName}
                              </span>
                            )}
                            {isOpportunity && opportunityCustomerName && (
                              <span className="text-xs font-normal text-purple-600 dark:text-purple-400 truncate">
                                @ {opportunityCustomerName}
                              </span>
                            )}
                            {isLead && leadCustomerName && leadCustomerName !== subInfo && (
                              <span className="text-xs font-normal text-green-600 dark:text-green-400 truncate">
                                @ {leadCustomerName}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              #{idString}
                            </span>
                            {subInfo && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                • {subInfo}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 ${selected ? `${itemColors.badge} border-transparent` : 'border-gray-300 dark:border-gray-600'}`}>
                        {selected && <Check className={`w-3.5 h-3.5 ${itemColors.icon}`} />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300"
            type="button"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-white ${confirmDisabled ? 'bg-blue-400 cursor-not-allowed opacity-70' : 'bg-blue-600 hover:bg-blue-700'}`}
            type="button"
            disabled={confirmDisabled}
          >
            {ConfirmIcon && <ConfirmIcon className="w-4 h-4" />}
            <span>{confirmText}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntitySelectionModal;

